import { z } from 'zod';
import { MetaSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * AgentForge contracts (ADR-0080) — custom and curated agents invited into a
 * workstream.
 *
 * Two layers, as for the workstreamagent:
 *   - the **invoke** contract the BFF sends the private `agentforge` Lambda
 *     (mirrors `apps/agentforge/api/src/agentforge_api/contracts.py`; strict-in,
 *     the reply is parsed tolerant-out), and
 *   - the **BFF↔SPA** `data` shapes of the member-agent routes, strict inside
 *     the loose `{ data, meta }` envelope (ADR-0024 §11).
 *
 * `orgId` is never client-supplied: the BFF takes it from verified claims.
 */

export const AGENTFORGE_CONTRACT_VERSION = 1 as const;

/** Mirrors the Python patterns; the agent id is also the `@mention` handle. */
export const AgentIdSchema = z.string().regex(/^[a-z][a-z0-9-]{1,39}$/);
const OrgIdSchema = z.string().regex(/^org_[A-Za-z0-9]{1,64}$/);
const WorkstreamIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

export const AgentForgeRequestSchema = z.strictObject({
  contractVersion: z.literal(AGENTFORGE_CONTRACT_VERSION),
  requestId: z.string().min(1).max(128),
  action: z.enum([
    'ping',
    'listAgents',
    'listMembers',
    'addMember',
    'removeMember',
    'removeAllMembers',
    'resolveMembers',
    // AB-2-2: builder workstreams and the caller's drafts.
    'openBuilder',
    'getBuilder',
    'listDrafts',
    // AB-2-4: the owner publishes the builder's latest revision / rolls back.
    'publishDraft',
    'setLiveVersion',
    // AB-2-5: the org library — share with review, retire.
    'listLibrary',
    'requestShare',
    'listReviews',
    'decideReview',
    'retireAgent',
  ]),
  orgId: OrgIdSchema.optional(),
  workstreamId: WorkstreamIdSchema.optional(),
  agentId: AgentIdSchema.optional(),
  invitedBy: z.string().min(1).max(256).optional(),
  /** AB-2-2: the caller's sub (verified claims) — a builder and its drafts are its opener's. */
  requestedBy: z.string().min(1).max(256).optional(),
  /** AB-2-4 `setLiveVersion`: the earlier version to roll back to. */
  agentVersion: z.number().int().positive().optional(),
  /** AB-2-5 `decideReview`: the Owner/Admin's outcome. */
  decision: z.enum(['approve', 'decline']).optional(),
  /** AB-2-5 `retireAgent`: the caller's tenant role as the BFF verified it. */
  actorRole: z.enum(['owner', 'admin', 'member']).optional(),
});
export type AgentForgeRequest = z.infer<typeof AgentForgeRequestSchema>;

/** Tolerant-out: the BFF reads only what it knows. `ok: false` carries `error`. */
export const AgentForgeResponseSchema = z.looseObject({
  ok: z.boolean(),
  requestId: z.string(),
  error: z.string().optional(),
});
export type AgentForgeResponse = z.infer<typeof AgentForgeResponseSchema>;

/** One of the org's shared agents a workstream member may invite. */
export const OrgAgentSchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  description: z.string().max(500),
  version: z.number().int().positive(),
});
export type OrgAgent = z.infer<typeof OrgAgentSchema>;

/** A member agent of one workstream, at the version a turn would run. */
export const MemberAgentSchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  version: z.number().int().positive(),
  mode: z.enum(['follow', 'pinned']),
  router: z.boolean(),
  invitedBy: z.string().min(1),
  invitedAt: Rfc3339DateTime,
});
export type MemberAgent = z.infer<typeof MemberAgentSchema>;

/** `data` of `GET /api/v1/workstreams/agents`. */
export const ListOrgAgentsDataSchema = z.strictObject({ agents: z.array(OrgAgentSchema) });
export type ListOrgAgentsData = z.infer<typeof ListOrgAgentsDataSchema>;
export const ListOrgAgentsResponseSchema = z.looseObject({
  data: ListOrgAgentsDataSchema,
  meta: MetaSchema,
});

/** `data` of `GET /api/v1/workstreams/{id}/agents`. */
export const ListMemberAgentsDataSchema = z.strictObject({ members: z.array(MemberAgentSchema) });
export type ListMemberAgentsData = z.infer<typeof ListMemberAgentsDataSchema>;
export const ListMemberAgentsResponseSchema = z.looseObject({
  data: ListMemberAgentsDataSchema,
  meta: MetaSchema,
});

/** `data` of `PUT /api/v1/workstreams/{id}/agents/{agentId}` (invite; idempotent). */
export const MemberAgentDataSchema = z.strictObject({ member: MemberAgentSchema });
export type MemberAgentData = z.infer<typeof MemberAgentDataSchema>;
export const MemberAgentResponseSchema = z.looseObject({
  data: MemberAgentDataSchema,
  meta: MetaSchema,
});

/** `data` of `DELETE /api/v1/workstreams/{id}/agents/{agentId}` — the removed id (idempotent). */
export const RemoveMemberAgentDataSchema = z.strictObject({ agentId: AgentIdSchema });
export type RemoveMemberAgentData = z.infer<typeof RemoveMemberAgentDataSchema>;
export const RemoveMemberAgentResponseSchema = z.looseObject({
  data: RemoveMemberAgentDataSchema,
  meta: MetaSchema,
});

// --- AB-2-2: builder workstreams + draft history ------------------------------------------

/** One immutable saved revision of an agent's draft (Forge writes; nobody edits). */
export const DraftRevisionSchema = z.strictObject({
  agentId: AgentIdSchema,
  revision: z.number().int().positive(),
  displayName: z.string().min(1).max(80),
  description: z.string().min(1).max(500),
  soul: z.string().min(1).max(20_000),
  /** Trigger type ids from AgentForge's platform catalogue (ADR-0081 §4) — `mention` today. */
  triggers: z.array(z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/)).max(10),
  trust: z.literal('human-in-the-loop'),
  evalQuestions: z.array(z.string()).min(1).max(10),
  forkedFrom: AgentIdSchema.optional(),
  updatedAt: Rfc3339DateTime,
});
export type DraftRevision = z.infer<typeof DraftRevisionSchema>;

/** ADR-0081 §1: `private` = published for its owner's own use (AB-2-4). */
export const AgentStatusSchema = z.enum(['draft', 'private', 'shared', 'retired']);

/** The agent a builder drafts — its lifecycle, never its owner's sub. */
export const BuilderAgentSchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  kind: z.enum(['custom', 'curated']),
  status: AgentStatusSchema,
  /** The live version — publish moves it forward, rollback back (AB-2-4). */
  latestApproved: z.number().int().positive().optional(),
});
export type BuilderAgent = z.infer<typeof BuilderAgentSchema>;

/** One published version (AB-2-4): its content is the revision it came from. */
export const PublishedVersionSchema = z.strictObject({
  version: z.number().int().positive(),
  publishedAt: Rfc3339DateTime,
  /** Absent on a seeded version, which no revision produced. */
  sourceRevision: z.number().int().positive().optional(),
});
export type PublishedVersion = z.infer<typeof PublishedVersionSchema>;

/** `data` of `GET /api/v1/workstreams/{id}/builder` — `agent` is null until Forge's first save. */
export const BuilderDataSchema = z.strictObject({
  agent: BuilderAgentSchema.nullable(),
  revisions: z.array(DraftRevisionSchema),
  versions: z.array(PublishedVersionSchema),
});
export type BuilderData = z.infer<typeof BuilderDataSchema>;
export const BuilderResponseSchema = z.looseObject({ data: BuilderDataSchema, meta: MetaSchema });

/** One of the caller's own drafts, as the Agents panel lists it. */
export const DraftSummarySchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  status: AgentStatusSchema,
  revision: z.number().int().positive(),
  updatedAt: Rfc3339DateTime,
  builderWorkstreamId: WorkstreamIdSchema.optional(),
});
export type DraftSummary = z.infer<typeof DraftSummarySchema>;

/** `data` of `GET /api/v1/workstreams/agents/drafts`. */
export const ListDraftsDataSchema = z.strictObject({ drafts: z.array(DraftSummarySchema) });
export type ListDraftsData = z.infer<typeof ListDraftsDataSchema>;
export const ListDraftsResponseSchema = z.looseObject({
  data: ListDraftsDataSchema,
  meta: MetaSchema,
});

/** `data` of `POST /api/v1/workstreams/builders` — the new builder workstream. */
export const CreateBuilderDataSchema = z.strictObject({
  workstreamId: WorkstreamIdSchema,
  createdAt: Rfc3339DateTime,
});
export type CreateBuilderData = z.infer<typeof CreateBuilderDataSchema>;
export const CreateBuilderResponseSchema = z.looseObject({
  data: CreateBuilderDataSchema,
  meta: MetaSchema,
});

// --- AB-2-4: publish + rollback (ADR-0081 §1) ----------------------------------------------

/**
 * `data` of `POST /api/v1/workstreams/{id}/builder/publish` (the builder's latest
 * revision → the next version, or back to its version after a rollback) and of
 * `PUT /api/v1/workstreams/{id}/builder/live/{version}` (rollback to an earlier one).
 */
export const LiveVersionDataSchema = z.strictObject({
  agent: BuilderAgentSchema,
  version: z.number().int().positive(),
});
export type LiveVersionData = z.infer<typeof LiveVersionDataSchema>;
export const LiveVersionResponseSchema = z.looseObject({
  data: LiveVersionDataSchema,
  meta: MetaSchema,
});

// --- AB-2-5: the org library — share with review, retire (ADR-0081 §2/§3) ------------------

/** One agent in the library: the org's shared agents plus the caller's own published ones. */
export const LibraryAgentSchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  description: z.string().max(500),
  status: AgentStatusSchema,
  /** The live version. */
  version: z.number().int().positive().optional(),
  /** The caller owns it (only they share or update it; they or an org Owner retire it). */
  mine: z.boolean(),
  /** A share request for this version is waiting on an Owner or Admin. */
  reviewVersion: z.number().int().positive().optional(),
});
export type LibraryAgent = z.infer<typeof LibraryAgentSchema>;

/** `data` of `GET /api/v1/workstreams/agents/library`. */
export const ListLibraryDataSchema = z.strictObject({ agents: z.array(LibraryAgentSchema) });
export type ListLibraryData = z.infer<typeof ListLibraryDataSchema>;
export const ListLibraryResponseSchema = z.looseObject({
  data: ListLibraryDataSchema,
  meta: MetaSchema,
});

/** `data` of `POST /api/v1/workstreams/agents/{agentId}/share` — the pending request. */
export const ShareRequestDataSchema = z.strictObject({
  review: z.strictObject({
    agentId: AgentIdSchema,
    version: z.number().int().positive(),
    requestedAt: Rfc3339DateTime,
  }),
});
export type ShareRequestData = z.infer<typeof ShareRequestDataSchema>;
export const ShareRequestResponseSchema = z.looseObject({
  data: ShareRequestDataSchema,
  meta: MetaSchema,
});

/** A share request as the reviewer sees it: the version asking to go live and, for an
 * update to a shared agent, the soul it would replace. */
export const ShareReviewSchema = z.strictObject({
  agentId: AgentIdSchema,
  displayName: z.string().min(1).max(80),
  description: z.string().max(500),
  soul: z.string().min(1).max(20_000),
  version: z.number().int().positive(),
  /** The requester's sub; the SPA names them from the members list. */
  requestedBy: z.string().min(1).max(256),
  requestedAt: Rfc3339DateTime,
  currentSoul: z.string().max(20_000).optional(),
  currentVersion: z.number().int().positive().optional(),
});
export type ShareReview = z.infer<typeof ShareReviewSchema>;

/** `data` of `GET /api/v1/workstreams/agents/reviews` (Owner/Admin). */
export const ListReviewsDataSchema = z.strictObject({ reviews: z.array(ShareReviewSchema) });
export type ListReviewsData = z.infer<typeof ListReviewsDataSchema>;
export const ListReviewsResponseSchema = z.looseObject({
  data: ListReviewsDataSchema,
  meta: MetaSchema,
});

/** `data` of `POST /api/v1/workstreams/agents/{agentId}/review/{decision}` (Owner/Admin). */
export const DecideReviewDataSchema = z.strictObject({
  decision: z.enum(['approved', 'declined']),
  agentId: AgentIdSchema,
  version: z.number().int().positive(),
});
export type DecideReviewData = z.infer<typeof DecideReviewDataSchema>;
export const DecideReviewResponseSchema = z.looseObject({
  data: DecideReviewDataSchema,
  meta: MetaSchema,
});

/** `data` of `POST /api/v1/workstreams/agents/{agentId}/retire` (the owner or an org Owner). */
export const RetireAgentDataSchema = z.strictObject({
  agentId: AgentIdSchema,
  removedFrom: z.number().int().nonnegative(),
  draftsDeleted: z.number().int().nonnegative(),
});
export type RetireAgentData = z.infer<typeof RetireAgentDataSchema>;
export const RetireAgentResponseSchema = z.looseObject({
  data: RetireAgentDataSchema,
  meta: MetaSchema,
});
